import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Check, ExternalLink, Percent, RefreshCw, Search, X } from 'lucide-react';
import apiClient from '../api/client';
import { useToast } from '../components/ui/Toast';
import { Skeleton } from '../components/ui/Skeleton';
import { PageHeader } from '../components/ui/PageHeader';
import { Button } from '../components/ui/Button';
import { OrderPicker, orderLabel, type PickableOrder } from '../components/OrderPicker';
import { useAnyPermission, usePermission } from '../auth/usePermission';
import { Permission } from '../auth/permissions';
import i18n from '../i18n';
import { formatEUR } from '../lib/format';
import { enumLabel } from '../lib/enumLabel';
import { Input } from '../components/ui/Input';

interface Request {
  id: string;
  order_id: string;
  status: string;
  requested_pct: string | number;
  request_reason: string;
  requested_by: string;
  requested_at: string;
  approved_pct: string | number | null;
  approval_reason: string | null;
  approved_by: string | null;
  approved_at: string | null;
  rejected_by?: string | null;
  rejected_at?: string | null;
  // Joined context (server-side) — an approver decides on the order, not on a UUID.
  order_number?: string | null;
  order_status?: string | null;
  subtotal_ht?: string | number | null;
  total_ttc?: string | number | null;
  customer_name?: string | null;
  requested_by_name?: string | null;
  requested_by_email?: string | null;
  requested_by_role?: string | null;
  approved_by_name?: string | null;
  approved_by_email?: string | null;
  rejected_by_name?: string | null;
  rejected_by_email?: string | null;
}

/** Reason length floor — mirrors `discountReasonMinLength` (Invariant #16, Zod + DB CHECK). */
const MIN_REASON = 10;

const DATETIME_FMT = new Intl.DateTimeFormat('fr-FR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

function when(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : DATETIME_FMT.format(d);
}

const who = (name?: string | null, email?: string | null, id?: string | null) =>
  name || email || (id ? id.slice(0, 8) : i18n.t('discounts.unknown'));

const pct = (v: string | number | null | undefined) => (v == null ? null : Number(v));

/**
 * Screen 25 — Discount requests (MGR/ADM approve ≤ cap, OWN unlimited).
 *
 * The screen opens on the approver's actual queue — every PENDING_APPROVAL request across all
 * orders — because that is the work. Scoping to a single order is a filter on top of that, and
 * the order is picked from the list (its id is a UUID nobody types), never keyed in.
 *
 * IT IS ALSO WHERE A REQUEST IS RAISED (Invariant #14). POST /discounts/request has existed
 * since WP-1 and nothing in the app ever called it: the counter's only way to move a price was
 * to type one at the till, which #14 has now closed. A screen that shows requests and cannot
 * make one would leave the counter with no lane at all, so the requester's half lives here,
 * against the order they picked — the same order the approver then decides on.
 */
export default function DiscountRequests() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canApprove = usePermission(Permission.DISCOUNT_APPROVE_LE_15);
  const canRequest = usePermission(Permission.DISCOUNT_REQUEST);
  const canSee = useAnyPermission(
    Permission.DISCOUNT_REQUEST,
    Permission.DISCOUNT_APPROVE_LE_15,
    Permission.DISCOUNT_APPROVE_UNLIMITED
  );
  // Drilling into one order means listing orders and reading that order's history — both are
  // gated on the ORDER_VIEW action, which an approver-only role (ADMIN) does not hold. Hide the
  // picker there rather than offering a button that can only end in a 403.
  const canPickOrder = useAnyPermission(Permission.ORDER_VIEW_ALL, Permission.ORDER_VIEW_ALL_TODAY);
  const [order, setOrder] = useState<PickableOrder | null>(null);
  const [picking, setPicking] = useState(false);
  const [rows, setRows] = useState<Request[] | null>(null);
  const [loading, setLoading] = useState(false);
  const { showApiError, push } = useToast();

  // One loader for both modes: the pending queue, or one order's full history.
  const load = useCallback(
    async (target: PickableOrder | null) => {
      if (!target && !canApprove) {
        // A requester (COUNTER) has no queue of its own — it can only look up an order.
        setRows([]);
        return;
      }
      setLoading(true);
      try {
        const url = target
          ? `/discounts/orders/${target.id}/requests`
          : '/discounts/requests/pending';
        const { data } = await apiClient.get<Request[]>(url);
        setRows(data);
      } catch (err) {
        showApiError(err);
        setRows([]);
      } finally {
        setLoading(false);
      }
    },
    [canApprove, showApiError]
  );

  useEffect(() => {
    if (canSee) void load(order);
  }, [canSee, order, load]);

  const decide = async (
    id: string,
    action: 'approve' | 'reject',
    reason: string,
    approvedPct?: number
  ) => {
    await apiClient.post(
      `/discounts/requests/${id}/${action}`,
      action === 'approve' ? { approvalReason: reason, approvedPct } : { reason },
      { headers: { 'X-Command-Id': crypto.randomUUID() } }
    );
    push({
      tone: 'success',
      message: action === 'approve' ? t('discounts.approved') : t('discounts.rejected'),
    });
    await load(order);
  };

  if (!canSee) {
    return (
      <div>
        <PageHeader title={t('discounts.title')} />
        <p className="text-sm text-muted-foreground">{t('discounts.noRole')}</p>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={t('discounts.title')}
        description={order ? t('discounts.descriptionOrder') : t('discounts.descriptionQueue')}
        actions={
          <>
            {canPickOrder && (
              <Button size="sm" variant="outline" onClick={() => setPicking(true)}>
                <Search className="h-4 w-4" /> {t('discounts.selectOrder')}
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => void load(order)}
              disabled={loading}
              aria-label={t('discounts.refresh')}
              title={t('discounts.refresh')}
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </>
        }
      />

      {order && canRequest && <RequestForm order={order} onCreated={() => void load(order)} />}

      {/* A requester who has not picked an order yet is told what to do, not shown an empty
          page: the request is raised ON an order, so the order comes first. */}
      {!order && canRequest && !canApprove && canPickOrder && (
        <p className="mb-4 text-sm text-muted-foreground">{t('discounts.pickToRequest')}</p>
      )}

      {order && (
        <div className="mb-4 flex flex-wrap items-center gap-2 text-sm">
          <span className="rounded bg-muted px-2 py-1">
            {t('discounts.orderChip')} <strong>{orderLabel(order)}</strong>
          </span>
          <Button size="sm" variant="ghost" onClick={() => navigate(`/orders/${order.id}`)}>
            <ExternalLink className="h-4 w-4" />
            {t('discounts.openOrder')}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setOrder(null)}>
            <X className="h-4 w-4" /> {t('discounts.backToQueue')}
          </Button>
        </div>
      )}

      {loading && rows === null && <Skeleton className="h-24 w-full" />}

      {rows && rows.length === 0 && (
        <p className="text-sm text-muted-foreground">
          {order
            ? t('discounts.emptyOrder')
            : canApprove
              ? t('discounts.emptyQueue')
              : t('discounts.emptyRequester')}
        </p>
      )}

      {rows && rows.length > 0 && (
        <ul className="space-y-2">
          {rows.map((r) => (
            <Card
              key={r.id}
              r={r}
              canApprove={canApprove}
              showOrder={!order}
              onOpenOrder={() => navigate(`/orders/${r.order_id}`)}
              onDecide={decide}
            />
          ))}
        </ul>
      )}

      {picking && (
        <OrderPicker
          title={t('discounts.chooseOrder')}
          onClose={() => setPicking(false)}
          onPick={(o) => {
            setOrder(o);
            setPicking(false);
          }}
        />
      )}
    </div>
  );
}

/**
 * Raise a discount request on the picked order (#14, #16).
 *
 * The percentage and the reason are all the server takes; everything else it derives or refuses
 * — the order must still be discountable (#17, 422 DISCOUNT_AFTER_PREPARATION), the reason must
 * reach the configured minimum (#16), and a percentage that would sell a line under cost is
 * refused outright. Those are server rules and are left to the server: the form's own checks are
 * only there so the obvious mistakes do not cost a round trip, and every refusal is surfaced
 * verbatim rather than swallowed.
 */
function RequestForm({ order, onCreated }: { order: PickableOrder; onCreated: () => void }) {
  const { t } = useTranslation();
  const { showApiError, push } = useToast();
  const [pctStr, setPctStr] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const value = Number(pctStr.replace(',', '.'));
  const pctOk = Number.isFinite(value) && value > 0 && value <= 100;
  const reasonOk = reason.trim().length >= MIN_REASON;

  const submit = async () => {
    setBusy(true);
    try {
      await apiClient.post(
        '/discounts/request',
        { orderId: order.id, pct: value, reason: reason.trim() },
        { headers: { 'X-Command-Id': crypto.randomUUID() } }
      );
      push({ tone: 'success', message: t('discounts.requested') });
      setPctStr('');
      setReason('');
      onCreated();
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mb-4 rounded border bg-card p-3">
      <div className="mb-2 flex items-center gap-2 text-sm font-medium">
        <Percent className="h-4 w-4" />
        {t('discounts.requestTitle', { order: orderLabel(order) })}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <label className="text-xs text-muted-foreground">
          {t('discounts.requestPct')}
          <Input
            type="number"
            min={0}
            max={100}
            step="0.01"
            value={pctStr}
            onChange={(e) => setPctStr(e.target.value)}
            className="ml-1 inline-flex h-8 w-20"
          />{' '}
          %
        </label>
        <Input
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={t('discounts.requestReason', { min: MIN_REASON })}
          className="h-8 w-72"
        />
        <Button size="sm" disabled={!pctOk || !reasonOk || busy} onClick={() => void submit()}>
          <Check className="h-4 w-4" />
          {t('discounts.requestSubmit')}
        </Button>
      </div>
      <p className="mt-1 text-[11px] text-muted-foreground">
        {!reasonOk && reason.length > 0
          ? t('discounts.reasonRequired', { min: MIN_REASON })
          : t('discounts.requestHint')}
      </p>
    </div>
  );
}

function Card({
  r,
  canApprove,
  showOrder,
  onOpenOrder,
  onDecide,
}: {
  r: Request;
  canApprove: boolean;
  showOrder: boolean;
  onOpenOrder: () => void;
  onDecide: (
    id: string,
    a: 'approve' | 'reject',
    reason: string,
    approvedPct?: number
  ) => Promise<void>;
}) {
  const { t } = useTranslation();
  const { showApiError } = useToast();
  const requested = pct(r.requested_pct) ?? 0;
  const [reason, setReason] = useState('');
  const [grantedPct, setGrantedPct] = useState(String(requested));
  const [busy, setBusy] = useState<'approve' | 'reject' | null>(null);

  const granted = Number(grantedPct);
  const reasonOk = reason.trim().length >= MIN_REASON;
  const pctOk = Number.isFinite(granted) && granted > 0 && granted <= 100;
  const pending = r.status === 'PENDING_APPROVAL';

  // Value of the discount on this order — what the decision actually costs.
  const base = Number(r.subtotal_ht ?? 0);
  const worth = base > 0 ? (base * requested) / 100 : null;

  const run = async (action: 'approve' | 'reject') => {
    setBusy(action);
    try {
      // Approving the requested percentage sends no override; a lowered one is sent explicitly.
      await onDecide(
        r.id,
        action,
        reason.trim(),
        action === 'approve' && granted !== requested ? granted : undefined
      );
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(null);
    }
  };

  return (
    <li className="rounded border bg-card p-3 text-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-base font-semibold">{requested} %</span>
            <StatusPill s={r.status} />
            {showOrder && (
              <button
                onClick={onOpenOrder}
                className="font-mono text-xs text-primary underline-offset-2 hover:underline"
              >
                {r.order_number ?? r.order_id.slice(0, 8)}
              </button>
            )}
            {r.customer_name && (
              <span className="text-xs text-muted-foreground">{r.customer_name}</span>
            )}
            {r.order_status && (
              <span className="rounded bg-muted px-1.5 py-0.5 text-[11px]">
                {enumLabel('orderStatus', r.order_status)}
              </span>
            )}
          </div>
          <div className="mt-0.5 text-xs text-muted-foreground">
            {t('discounts.askedBy', {
              who: who(r.requested_by_name, r.requested_by_email, r.requested_by),
            })}
            {r.requested_by_role
              ? ` (${t(`roles.${r.requested_by_role}`, { defaultValue: r.requested_by_role })})`
              : ''}{' '}
            · {when(r.requested_at)}
            {worth != null &&
              ` · ${t('discounts.worth', { amount: formatEUR(worth), base: formatEUR(base) })}`}
          </div>
          <div className="mt-1">{r.request_reason}</div>
          {r.status === 'APPROVED' && (
            <div className="mt-1 text-xs text-success">
              {t('discounts.approvedLine', {
                pct: pct(r.approved_pct),
                who: who(r.approved_by_name, r.approved_by_email, r.approved_by),
              })}{' '}
              · {when(r.approved_at)}
              {r.approval_reason ? ` — ${r.approval_reason}` : ''}
            </div>
          )}
          {r.status === 'REJECTED' && (
            <div className="mt-1 text-xs text-destructive">
              {t('discounts.rejectedLine', {
                who: who(r.rejected_by_name, r.rejected_by_email, r.rejected_by),
              })}{' '}
              · {when(r.rejected_at)}
              {r.approval_reason ? ` — ${r.approval_reason}` : ''}
            </div>
          )}
        </div>

        {pending && canApprove && (
          <div className="flex flex-col items-end gap-1">
            <div className="flex flex-wrap items-center justify-end gap-2">
              <label className="text-xs text-muted-foreground">
                {t('discounts.grant')}
                <Input
                  type="number"
                  min={0}
                  max={100}
                  step="0.01"
                  value={grantedPct}
                  onChange={(e) => setGrantedPct(e.target.value)}
                  className="ml-1 inline-flex h-8 w-16"
                />{' '}
                %
              </label>
              <Input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={t('discounts.decisionReason', { min: MIN_REASON })}
                className="h-8 w-56"
              />
              <Button
                size="sm"
                variant="success"
                disabled={!reasonOk || !pctOk || busy !== null}
                onClick={() => void run('approve')}
              >
                <Check className="h-4 w-4" />
                {t('common.approve')}
              </Button>
              <Button
                size="sm"
                variant="destructive"
                disabled={!reasonOk || busy !== null}
                onClick={() => void run('reject')}
              >
                <X className="h-4 w-4" />
                {t('common.reject')}
              </Button>
            </div>
            {!reasonOk && (
              <p className="text-[11px] text-muted-foreground">
                {t('discounts.reasonRequired', { min: MIN_REASON })}
              </p>
            )}
          </div>
        )}
        {pending && !canApprove && (
          <span className="text-xs text-muted-foreground">{t('discounts.awaitingManager')}</span>
        )}
      </div>
    </li>
  );
}

function StatusPill({ s }: { s: string }) {
  const cls =
    (
      {
        PENDING_APPROVAL: 'bg-warning/20 text-warning',
        APPROVED: 'bg-success/20 text-success',
        REJECTED: 'bg-destructive/10 text-destructive',
      } as Record<string, string>
    )[s] ?? 'bg-muted text-foreground';
  return (
    <span className={`rounded px-2 py-0.5 text-xs ${cls}`}>
      {i18n.t(`discounts.status.${s}`, { defaultValue: s })}
    </span>
  );
}
